import { gql } from '@apollo/client';

const USER_FIELDS = `id name email bio interests avatarColor age ageGroup theme role language currency locale unreadCount tipsEarned walletCoins referralCode storePersona sellerRatingAvg sellerRatingCount upiId createdAt
  location { building neighborhood city country postalCode lat lng timezone }
  children { id name age ageGroup avatarColor walletCoins }`;

const GROUP_FIELDS = `id name description category tags maxMembers memberCount isOpen isMember isPrivate requiresApproval isGroupAdmin persona broadCategory imageUrl features createdAt
  ageGroups
  location { building neighborhood city country postalCode }
  schedule { day time frequency duration nextSession }
  creator { id name avatarColor ageGroup tipsEarned }
  members { id name avatarColor ageGroup age bio interests }`;

const EVENT_FIELDS = `id groupId title description videoUrl startsAt durationMins capacity ticketPrice status registrationCount isRegistered createdAt
  creator { id name avatarColor }`;

const PRODUCT_FIELDS = `id groupId name description price productType imageEmoji imageUrl availableOutside pickupAddress pickupLat pickupLng foodCategory stock unitCost persona createdAt
  creator { id name avatarColor sellerRatingAvg sellerRatingCount upiId }`;

const CAMPAIGN_FIELDS = `id groupId title goal description targetAgeGroups targetCity startDate endDate createdAt
  creator { id name avatarColor }`;

export const ME_QUERY = gql`query Me { me { ${USER_FIELDS} joinedGroups { id name category memberCount maxMembers isOpen } } }`;

export const GROUPS_QUERY = gql`query Groups($category:String,$search:String,$city:String,$building:String,$ageGroup:AgeGroup,$broadCategory:String,$persona:String) {
  groups(category:$category,search:$search,city:$city,building:$building,ageGroup:$ageGroup,broadCategory:$broadCategory,persona:$persona) { ${GROUP_FIELDS} }
}`;

export const GROUP_QUERY = gql`query Group($id:ID!) {
  group(id:$id) {
    ${GROUP_FIELDS}
    messages { id content messageType videoUrl createdAt groupId recipientId sender { id name avatarColor ageGroup } recipient { id name } }
    events { ${EVENT_FIELDS} }
    products { ${PRODUCT_FIELDS} }
    campaigns { ${CAMPAIGN_FIELDS} }
    webinars { id title description meetingUrl startsAt durationMins status rewardTotal attendeeCount isAttending host { id name avatarColor } }
  }
}`;

export const USER_QUERY = gql`query User($id:ID!) {
  user(id:$id) { ${USER_FIELDS} joinedGroups { id name category memberCount maxMembers isOpen } }
}`;

export const FIND_FOLKS_QUERY = gql`query FindFolks($interests:[String!],$city:String,$building:String,$neighborhood:String,$ageGroup:AgeGroup) {
  findFolks(interests:$interests,city:$city,building:$building,neighborhood:$neighborhood,ageGroup:$ageGroup) {
    sharedInterests proximity buddyStatus
    user { id name bio interests avatarColor age ageGroup tipsEarned location { building neighborhood city } joinedGroups { id name } }
  }
}`;

export const MY_NOTIFICATIONS_QUERY = gql`query MyNotifications {
  myNotifications { id type title message isRead groupId actorId scheduledFor createdAt }
}`;

export const MY_WALLET_QUERY = gql`query MyWallet { myWallet { userId coins } }`;

export const GROUP_EVENTS_QUERY = gql`query GroupEvents($groupId:ID!) {
  groupEvents(groupId:$groupId) { ${EVENT_FIELDS} }
}`;

// City-wide event discovery (Meetup-style) — events across public groups in a city.
const CITY_EVENT_FIELDS = `${EVENT_FIELDS} groupName groupCategory groupCity persona broadCategory creator { id name avatarColor }`;
export const CITY_EVENTS_QUERY = gql`query CityEvents($city:String,$category:String,$limit:Int) {
  cityEvents(city:$city,category:$category,limit:$limit) { ${CITY_EVENT_FIELDS} }
}`;
export const EVENT_CITIES_QUERY = gql`query EventCities { eventCities }`;

export const GROUP_PRODUCTS_QUERY = gql`query GroupProducts($groupId:ID!) {
  groupProducts(groupId:$groupId) { ${PRODUCT_FIELDS} }
}`;

export const GROUP_CAMPAIGNS_QUERY = gql`query GroupCampaigns($groupId:ID!) {
  groupCampaigns(groupId:$groupId) { ${CAMPAIGN_FIELDS} }
}`;

export const REGISTER_MUTATION = gql`mutation Register($name:String!,$email:String!,$password:String!,$age:Int,$city:String,$country:String,$building:String,$neighborhood:String,$currency:String,$locale:String,$language:String,$postalCode:String,$timezone:String,$referralCode:String) {
  register(name:$name,email:$email,password:$password,age:$age,city:$city,country:$country,building:$building,neighborhood:$neighborhood,currency:$currency,locale:$locale,language:$language,postalCode:$postalCode,timezone:$timezone,referralCode:$referralCode) {
    token refreshToken user { ${USER_FIELDS} joinedGroups { id name } }
  }
}`;

export const LOGIN_MUTATION = gql`mutation Login($email:String!,$password:String!) {
  login(email:$email,password:$password) { token refreshToken user { ${USER_FIELDS} joinedGroups { id name } } }
}`;

export const LOGIN_WITH_GOOGLE_MUTATION = gql`mutation LoginWithGoogle($idToken:String!) {
  loginWithGoogle(idToken:$idToken) { token refreshToken user { ${USER_FIELDS} joinedGroups { id name } } }
}`;

export const REFRESH_TOKEN_MUTATION = gql`mutation RefreshToken($refreshToken:String!) {
  refreshToken(refreshToken:$refreshToken) { token refreshToken user { ${USER_FIELDS} } }
}`;

export const CREATE_GROUP_MUTATION = gql`mutation CreateGroup($name:String!,$description:String!,$category:String!,$tags:[String!],$maxMembers:Int!,$ageGroups:[AgeGroup!],$city:String,$building:String,$neighborhood:String,$country:String,$postalCode:String,$scheduleDay:String,$scheduleTime:String,$scheduleFrequency:String,$scheduleDuration:Int,$persona:String,$broadCategory:String,$imageUrl:String,$features:[String!],$isPrivate:Boolean) {
  createGroup(name:$name,description:$description,category:$category,tags:$tags,maxMembers:$maxMembers,ageGroups:$ageGroups,city:$city,building:$building,neighborhood:$neighborhood,country:$country,postalCode:$postalCode,scheduleDay:$scheduleDay,scheduleTime:$scheduleTime,scheduleFrequency:$scheduleFrequency,scheduleDuration:$scheduleDuration,persona:$persona,broadCategory:$broadCategory,imageUrl:$imageUrl,features:$features,isPrivate:$isPrivate) {
    ${GROUP_FIELDS}
  }
}`;

export const SET_GROUP_IMAGE_MUTATION = gql`mutation SetGroupImage($groupId:ID!,$imageUrl:String!) {
  setGroupImage(groupId:$groupId,imageUrl:$imageUrl) { id imageUrl }
}`;

/* ═══════════════════════════════════════ RAZORPAY ════════════════════════════ */

export const CREATE_RAZORPAY_ORDER_MUTATION = gql`mutation CreateRazorpayOrder($amount:Int!,$currency:String,$purpose:String) {
  createRazorpayOrder(amount:$amount,currency:$currency,purpose:$purpose) { orderId amount currency keyId purpose }
}`;

export const BUY_WITH_RAZORPAY_MUTATION = gql`mutation BuyWithRazorpay($productId:ID!,$quantity:Int,$razorpayOrderId:String!,$razorpayPaymentId:String!,$signature:String!,$deliveryAddress:String) {
  buyProductWithRazorpay(productId:$productId,quantity:$quantity,razorpayOrderId:$razorpayOrderId,razorpayPaymentId:$razorpayPaymentId,signature:$signature,deliveryAddress:$deliveryAddress) {
    id totalCoins totalAmount currency paymentMethod status product { id name imageEmoji imageUrl }
  }
}`;

export const BUY_WITH_UPI_MUTATION = gql`mutation BuyWithUpi($productId:ID!,$quantity:Int,$upiRef:String,$deliveryAddress:String) {
  buyProductWithUpi(productId:$productId,quantity:$quantity,upiRef:$upiRef,deliveryAddress:$deliveryAddress) {
    id totalCoins totalAmount currency paymentMethod status product { id name imageEmoji imageUrl }
  }
}`;

export const BUY_WITH_COD_MUTATION = gql`
  mutation BuyProductWithCOD($productId: ID!, $quantity: Int!, $deliveryAddress: String!) {
    buyProductWithCOD(productId: $productId, quantity: $quantity, deliveryAddress: $deliveryAddress) {
      id status paymentMethod totalAmount totalCoins currency
    }
  }
`;

export const JOIN_GROUP_MUTATION = gql`mutation JoinGroup($groupId:ID!) {
  joinGroup(groupId:$groupId) { id memberCount isOpen isMember members { id name avatarColor } }
}`;

export const LEAVE_GROUP_MUTATION = gql`mutation LeaveGroup($groupId:ID!) {
  leaveGroup(groupId:$groupId) { id memberCount isOpen isMember members { id name avatarColor } }
}`;

export const SEND_MESSAGE_MUTATION = gql`mutation SendMessage($groupId:ID!,$content:String!,$messageType:MessageType,$videoUrl:String,$recipientId:ID) {
  sendMessage(groupId:$groupId,content:$content,messageType:$messageType,videoUrl:$videoUrl,recipientId:$recipientId) { id content messageType videoUrl createdAt groupId recipientId sender { id name avatarColor } recipient { id name } }
}`;

export const UPDATE_PROFILE_MUTATION = gql`mutation UpdateProfile($bio:String,$interests:[String!],$age:Int,$building:String,$neighborhood:String,$city:String,$country:String,$postalCode:String,$language:String,$theme:Theme,$currency:String,$locale:String,$timezone:String,$lat:Float,$lng:Float,$upiId:String) {
  updateProfile(bio:$bio,interests:$interests,age:$age,building:$building,neighborhood:$neighborhood,city:$city,country:$country,postalCode:$postalCode,language:$language,theme:$theme,currency:$currency,locale:$locale,timezone:$timezone,lat:$lat,lng:$lng,upiId:$upiId) {
    ${USER_FIELDS}
  }
}`;

export const SEND_BUDDY_MUTATION = gql`mutation SendBuddy($toUserId:ID!) { sendBuddyRequest(toUserId:$toUserId) }`;
export const MARK_READ_MUTATION = gql`mutation MarkRead { markNotificationsRead }`;

export const SEND_TIP_MUTATION = gql`mutation SendTip($toUserId:ID!,$groupId:ID,$amount:Int!,$message:String) {
  sendTip(toUserId:$toUserId,groupId:$groupId,amount:$amount,message:$message) { id amount message createdAt }
}`;

export const REGISTER_FOR_EVENT_MUTATION = gql`mutation RegisterForEvent($eventId:ID!) {
  registerForEvent(eventId:$eventId) { ${EVENT_FIELDS} }
}`;

export const UNREGISTER_FROM_EVENT_MUTATION = gql`mutation UnregisterFromEvent($eventId:ID!) {
  unregisterFromEvent(eventId:$eventId) { ${EVENT_FIELDS} }
}`;

export const CREATE_EVENT_MUTATION = gql`mutation CreateEvent($groupId:ID!,$title:String!,$description:String,$videoUrl:String,$startsAt:String!,$durationMins:Int,$capacity:Int,$ticketPrice:Int) {
  createEvent(groupId:$groupId,title:$title,description:$description,videoUrl:$videoUrl,startsAt:$startsAt,durationMins:$durationMins,capacity:$capacity,ticketPrice:$ticketPrice) { ${EVENT_FIELDS} }
}`;

export const CREATE_PRODUCT_MUTATION = gql`mutation CreateProduct($groupId:ID!,$name:String!,$description:String,$price:Int!,$productType:ProductType,$imageEmoji:String,$imageUrl:String,$availableOutside:Boolean,$pickupAddress:String,$pickupLat:Float,$pickupLng:Float,$foodCategory:String,$stock:Int,$unitCost:Int,$persona:String) {
  createProduct(groupId:$groupId,name:$name,description:$description,price:$price,productType:$productType,imageEmoji:$imageEmoji,imageUrl:$imageUrl,availableOutside:$availableOutside,pickupAddress:$pickupAddress,pickupLat:$pickupLat,pickupLng:$pickupLng,foodCategory:$foodCategory,stock:$stock,unitCost:$unitCost,persona:$persona) { ${PRODUCT_FIELDS} }
}`;

export const CREATE_CAMPAIGN_MUTATION = gql`mutation CreateCampaign($groupId:ID!,$title:String!,$goal:CampaignGoal,$description:String,$targetAgeGroups:[AgeGroup!],$targetCity:String,$startDate:String!,$endDate:String!) {
  createCampaign(groupId:$groupId,title:$title,goal:$goal,description:$description,targetAgeGroups:$targetAgeGroups,targetCity:$targetCity,startDate:$startDate,endDate:$endDate) { ${CAMPAIGN_FIELDS} }
}`;

export const ADD_COINS_MUTATION = gql`mutation AddCoins($userId:ID!,$amount:Int!) {
  addCoins(userId:$userId,amount:$amount) { userId coins }
}`;

export const LINK_CHILD_MUTATION = gql`mutation LinkChild($childEmail:String!) {
  linkChild(childEmail:$childEmail) { id name age ageGroup avatarColor walletCoins }
}`;

export const MESSAGE_SUBSCRIPTION = gql`subscription MessageSent($groupId:ID!) {
  messageSent(groupId:$groupId) { id content messageType videoUrl createdAt groupId recipientId sender { id name avatarColor } recipient { id name } }
}`;

export const GROUP_MEMBER_SUBSCRIPTION = gql`subscription GroupMemberChanged($groupId:ID!) {
  groupMemberChanged(groupId:$groupId) { id memberCount isOpen members { id name avatarColor } }
}`;

export const NOTIFICATION_SUBSCRIPTION = gql`subscription NotificationReceived {
  notificationReceived { id type title message isRead groupId createdAt }
}`;

/* ═══════════════════════════════════════ EXPERT NETWORK ══════════════════════ */

const EXPERT_FIELDS = `id userId headline bio skills serviceType hourlyRate currency
  languages countries isElderSupport isVerified persona phone address ratingAvg ratingCount totalSessions availability createdAt
  user { id name avatarColor ageGroup age location { city country building } }
  reviews { id rating comment createdAt reviewer { id name avatarColor } }`;

const BOOKING_FIELDS = `id skill serviceType scheduledAt durationMins amount currency status notes meetingUrl createdAt
  expert { id userId headline skills ratingAvg user { id name avatarColor } }
  user { id name avatarColor }`;

export const SEARCH_EXPERTS_QUERY = gql`query SearchExperts($skill:String,$isElderSupport:Boolean,$country:String,$serviceType:ServiceType) {
  searchExperts(skill:$skill,isElderSupport:$isElderSupport,country:$country,serviceType:$serviceType) { ${EXPERT_FIELDS} }
}`;

export const EXPERT_QUERY = gql`query Expert($id:ID!) { expert(id:$id) { ${EXPERT_FIELDS} } }`;

export const MY_EXPERT_PROFILE_QUERY = gql`query MyExpertProfile { myExpertProfile { ${EXPERT_FIELDS} } }`;

export const MY_BOOKINGS_QUERY = gql`query MyBookings { myBookings { ${BOOKING_FIELDS} } }`;

export const EXPERT_BOOKINGS_QUERY = gql`query ExpertBookings { expertBookings { ${BOOKING_FIELDS} } }`;

export const REGISTER_AS_SELLER_MUTATION = gql`mutation RegisterAsSeller($storeName:String!,$description:String,$persona:String,$upiId:String) {
  registerAsSeller(storeName:$storeName,description:$description,persona:$persona,upiId:$upiId) { ${USER_FIELDS} joinedGroups { id name } }
}`;

export const REGISTER_AS_EXPERT_MUTATION = gql`mutation RegisterAsExpert($headline:String!,$bio:String,$skills:[String!]!,$serviceType:ServiceType,$hourlyRate:Int,$currency:String,$languages:[String!],$countries:[String!],$isElderSupport:Boolean,$availability:String,$persona:String,$phone:String,$address:String) {
  registerAsExpert(headline:$headline,bio:$bio,skills:$skills,serviceType:$serviceType,hourlyRate:$hourlyRate,currency:$currency,languages:$languages,countries:$countries,isElderSupport:$isElderSupport,availability:$availability,persona:$persona,phone:$phone,address:$address) { ${EXPERT_FIELDS} }
}`;

export const UPDATE_EXPERT_PROFILE_MUTATION = gql`mutation UpdateExpertProfile($headline:String,$bio:String,$skills:[String!],$serviceType:ServiceType,$hourlyRate:Int,$currency:String,$languages:[String!],$countries:[String!],$isElderSupport:Boolean,$availability:String,$persona:String,$phone:String,$address:String) {
  updateExpertProfile(headline:$headline,bio:$bio,skills:$skills,serviceType:$serviceType,hourlyRate:$hourlyRate,currency:$currency,languages:$languages,countries:$countries,isElderSupport:$isElderSupport,availability:$availability,persona:$persona,phone:$phone,address:$address) { ${EXPERT_FIELDS} }
}`;

export const BOOK_EXPERT_MUTATION = gql`mutation BookExpert($expertId:ID!,$skill:String!,$serviceType:ServiceType,$scheduledAt:String!,$durationMins:Int,$notes:String) {
  bookExpert(expertId:$expertId,skill:$skill,serviceType:$serviceType,scheduledAt:$scheduledAt,durationMins:$durationMins,notes:$notes) { ${BOOKING_FIELDS} }
}`;

export const CONFIRM_BOOKING_MUTATION = gql`mutation ConfirmBooking($bookingId:ID!,$meetingUrl:String) {
  confirmBooking(bookingId:$bookingId,meetingUrl:$meetingUrl) { ${BOOKING_FIELDS} }
}`;

export const CANCEL_BOOKING_MUTATION = gql`mutation CancelBooking($bookingId:ID!) {
  cancelBooking(bookingId:$bookingId) { ${BOOKING_FIELDS} }
}`;

export const COMPLETE_BOOKING_MUTATION = gql`mutation CompleteBooking($bookingId:ID!) {
  completeBooking(bookingId:$bookingId) { ${BOOKING_FIELDS} }
}`;

export const REVIEW_EXPERT_MUTATION = gql`mutation ReviewExpert($expertId:ID!,$bookingId:ID!,$rating:Int!,$comment:String) {
  reviewExpert(expertId:$expertId,bookingId:$bookingId,rating:$rating,comment:$comment) { id ratingAvg ratingCount reviews { id rating comment createdAt reviewer { id name } } }
}`;

/* ═══════════════════════════════════════ ADMIN / SELLER ══════════════════════ */

export const ADMIN_STATS_QUERY = gql`query AdminStats {
  adminStats {
    totalUsers totalGroups totalMessages totalExperts totalBookings totalCoupons
    recentUsers { id name email role createdAt avatarColor ageGroup location { city country } }
    recentGroups { id name category memberCount maxMembers createdAt }
  }
}`;

export const ADMIN_USERS_QUERY = gql`query AdminUsers($search:String,$role:UserRole) {
  adminUsers(search:$search,role:$role) { id name email role ageGroup createdAt avatarColor location { city country } }
}`;

export const SET_USER_ROLE_MUTATION = gql`mutation SetUserRole($userId:ID!,$role:UserRole!) {
  setUserRole(userId:$userId,role:$role) { id name email role }
}`;

export const SEED_GROUPS_MUTATION = gql`mutation SeedGroups { seedGroups }`;

const COUPON_FIELDS = `id code description discountPct maxUses usedCount groupId expiresAt isActive isFlashOffer offerLabel createdAt seller { id name avatarColor }`;

export const MY_COUPONS_QUERY = gql`query MyCoupons { myCoupons { ${COUPON_FIELDS} } }`;

export const COUPON_QUERY = gql`query Coupon($code:String!) { coupon(code:$code) { ${COUPON_FIELDS} } }`;

export const CREATE_COUPON_MUTATION = gql`mutation CreateCoupon($code:String!,$description:String!,$discountPct:Int!,$maxUses:Int!,$groupId:ID,$expiresAt:String,$isFlashOffer:Boolean,$offerLabel:String) {
  createCoupon(code:$code,description:$description,discountPct:$discountPct,maxUses:$maxUses,groupId:$groupId,expiresAt:$expiresAt,isFlashOffer:$isFlashOffer,offerLabel:$offerLabel) { ${COUPON_FIELDS} }
}`;

export const DELETE_COUPON_MUTATION = gql`mutation DeleteCoupon($id:ID!) { deleteCoupon(id:$id) }`;

export const SET_FLASH_OFFER_MUTATION = gql`mutation SetFlashOffer($couponId:ID!,$isFlashOffer:Boolean!,$offerLabel:String) {
  setFlashOffer(couponId:$couponId,isFlashOffer:$isFlashOffer,offerLabel:$offerLabel) { ${COUPON_FIELDS} }
}`;

/* ═══════════════════════════════════════ LEARNING LIBRARY ════════════════════ */

const LC_FIELDS = `id contentType title body mediaUrl thumbnailUrl category tags viewCount createdAt author { id name avatarColor role }`;

export const LEARNING_CONTENT_QUERY = gql`query LearningContent($category:String,$contentType:ContentType,$search:String) {
  learningContent(category:$category,contentType:$contentType,search:$search) { ${LC_FIELDS} }
}`;

export const LEARNING_CONTENT_ITEM_QUERY = gql`query LearningContentItem($id:ID!) {
  learningContentItem(id:$id) { ${LC_FIELDS} }
}`;

export const CREATE_LEARNING_CONTENT_MUTATION = gql`mutation CreateLearningContent($contentType:ContentType!,$title:String!,$body:String,$mediaUrl:String,$thumbnailUrl:String,$category:String,$tags:[String!]) {
  createLearningContent(contentType:$contentType,title:$title,body:$body,mediaUrl:$mediaUrl,thumbnailUrl:$thumbnailUrl,category:$category,tags:$tags) { ${LC_FIELDS} }
}`;

export const DELETE_LEARNING_CONTENT_MUTATION = gql`mutation DeleteLearningContent($id:ID!) { deleteLearningContent(id:$id) }`;

/* ═══════════════════════════════════════ WEBINARS ════════════════════════════ */

const WEBINAR_FIELDS = `id groupId title description meetingUrl startsAt durationMins maxAttendees status rewardTotal attendeeCount isAttending createdAt
  host { id name avatarColor ageGroup tipsEarned }
  attendees { id name avatarColor }`;

export const GROUP_WEBINARS_QUERY = gql`query GroupWebinars($groupId:ID!) {
  groupWebinars(groupId:$groupId) { ${WEBINAR_FIELDS} }
}`;

export const CREATE_WEBINAR_MUTATION = gql`mutation CreateWebinar($groupId:ID!,$title:String!,$description:String,$meetingUrl:String,$startsAt:String!,$durationMins:Int,$maxAttendees:Int) {
  createWebinar(groupId:$groupId,title:$title,description:$description,meetingUrl:$meetingUrl,startsAt:$startsAt,durationMins:$durationMins,maxAttendees:$maxAttendees) { ${WEBINAR_FIELDS} }
}`;

export const JOIN_WEBINAR_MUTATION = gql`mutation JoinWebinar($webinarId:ID!) { joinWebinar(webinarId:$webinarId) { ${WEBINAR_FIELDS} } }`;
export const LEAVE_WEBINAR_MUTATION = gql`mutation LeaveWebinar($webinarId:ID!) { leaveWebinar(webinarId:$webinarId) { ${WEBINAR_FIELDS} } }`;
export const START_WEBINAR_MUTATION = gql`mutation StartWebinar($webinarId:ID!,$meetingUrl:String) { startWebinar(webinarId:$webinarId,meetingUrl:$meetingUrl) { ${WEBINAR_FIELDS} } }`;
export const END_WEBINAR_MUTATION = gql`mutation EndWebinar($webinarId:ID!) { endWebinar(webinarId:$webinarId) { ${WEBINAR_FIELDS} } }`;
export const REWARD_HOST_MUTATION = gql`mutation RewardHost($webinarId:ID!,$amount:Int!,$message:String) { rewardWebinarHost(webinarId:$webinarId,amount:$amount,message:$message) { ${WEBINAR_FIELDS} } }`;

/* ═══════════════════════════════════════ PRIVACY / COMPLIANCE ════════════ */

const PRIVACY_FIELDS = `userId termsAcceptedAt privacyAcceptedAt marketingConsent analyticsConsent doNotSell dataProcessingConsent lastUpdated
  cookiePreferences { necessary analytics marketing }`;

export const MY_PRIVACY_SETTINGS_QUERY = gql`query MyPrivacySettings {
  myPrivacySettings { ${PRIVACY_FIELDS} }
}`;

export const UPDATE_PRIVACY_SETTINGS_MUTATION = gql`mutation UpdatePrivacySettings($marketingConsent:Boolean,$analyticsConsent:Boolean,$doNotSell:Boolean,$dataProcessingConsent:Boolean,$cookiePreferences:String) {
  updatePrivacySettings(marketingConsent:$marketingConsent,analyticsConsent:$analyticsConsent,doNotSell:$doNotSell,dataProcessingConsent:$dataProcessingConsent,cookiePreferences:$cookiePreferences) { ${PRIVACY_FIELDS} }
}`;

export const REQUEST_DATA_EXPORT_MUTATION = gql`mutation RequestDataExport { requestDataExport }`;
export const DELETE_MY_ACCOUNT_MUTATION = gql`mutation DeleteMyAccount($password:String!) { deleteMyAccount(password:$password) }`;
export const RECORD_CONSENT_MUTATION = gql`mutation RecordConsent($termsAccepted:Boolean,$privacyAccepted:Boolean) {
  recordConsent(termsAccepted:$termsAccepted,privacyAccepted:$privacyAccepted) { ${PRIVACY_FIELDS} }
}`;

/* ═══════════════════════════════════════ GROUP ADMIN ═════════════════════ */

export const DELETE_GROUP_MUTATION = gql`mutation DeleteGroup($groupId:ID!) { deleteGroup(groupId:$groupId) }`;
export const MAKE_GROUP_PRIVATE_MUTATION = gql`mutation MakeGroupPrivate($groupId:ID!,$isPrivate:Boolean!) {
  makeGroupPrivate(groupId:$groupId,isPrivate:$isPrivate) { id isPrivate isGroupAdmin }
}`;

// ── Approval-required groups ──
const JOIN_REQ_FIELDS = `id groupId userId status message createdAt userName userAvatar`;
export const SET_GROUP_APPROVAL_MUTATION = gql`mutation SetGroupApproval($groupId:ID!,$requiresApproval:Boolean!) {
  setGroupApproval(groupId:$groupId,requiresApproval:$requiresApproval) { id requiresApproval isGroupAdmin }
}`;
export const REQUEST_TO_JOIN_MUTATION = gql`mutation RequestToJoinGroup($groupId:ID!,$message:String) {
  requestToJoinGroup(groupId:$groupId,message:$message) { ${JOIN_REQ_FIELDS} }
}`;
export const GROUP_JOIN_REQUESTS_QUERY = gql`query GroupJoinRequests($groupId:ID!) {
  groupJoinRequests(groupId:$groupId) { ${JOIN_REQ_FIELDS} }
}`;
export const RESPOND_JOIN_REQUEST_MUTATION = gql`mutation RespondToJoinRequest($requestId:ID!,$approve:Boolean!) {
  respondToJoinRequest(requestId:$requestId,approve:$approve) { id status }
}`;
export const ASSIGN_GROUP_ADMIN_MUTATION = gql`mutation AssignGroupAdmin($groupId:ID!,$userId:ID!) {
  assignGroupAdmin(groupId:$groupId,userId:$userId) { id isGroupAdmin members { id name avatarColor } }
}`;

export const INVITE_TO_GROUP_MUTATION = gql`
  mutation InviteToGroup($groupId: ID!, $emails: [String!]!, $message: String) {
    inviteToGroup(groupId: $groupId, emails: $emails, message: $message) {
      id invitedEmail status
    }
  }
`;

export const SUBMIT_FEEDBACK_MUTATION = gql`
  mutation SubmitFeedback($groupId: ID, $eventId: ID, $rating: Int!, $comment: String) {
    submitFeedback(groupId: $groupId, eventId: $eventId, rating: $rating, comment: $comment) {
      id rating
    }
  }
`;

/* ═══════════════════════════════════════ PAYMENTS ════════════════════════ */

export const CREATE_PAYMENT_INTENT_MUTATION = gql`mutation CreatePaymentIntent($amount:Int!,$currency:String,$description:String) {
  createPaymentIntent(amount:$amount,currency:$currency,description:$description) { clientSecret paymentIntentId amount currency }
}`;
export const CONFIRM_TIP_PAYMENT_MUTATION = gql`mutation ConfirmTipPayment($paymentIntentId:String!,$toUserId:ID!,$groupId:ID,$amount:Int!,$message:String) {
  confirmTipPayment(paymentIntentId:$paymentIntentId,toUserId:$toUserId,groupId:$groupId,amount:$amount,message:$message) { id amount message }
}`;

/* ═══════════════════════════════════════ WALLET / ORDERS ═════════════════════ */

export const MY_WALLET_FULL_QUERY = gql`query MyWalletFull {
  myWallet { userId coins coinValueLocal coinCurrency }
  myOrders { id productId quantity totalCoins totalAmount currency paymentMethod status deliveryAddress createdAt product { id name imageEmoji imageUrl price productType } }
}`;

export const BUY_WITH_COINS_MUTATION = gql`mutation BuyWithCoins($productId:ID!,$quantity:Int,$deliveryAddress:String) {
  buyProductWithCoins(productId:$productId,quantity:$quantity,deliveryAddress:$deliveryAddress) {
    id totalCoins totalAmount currency paymentMethod status product { id name imageEmoji imageUrl }
  }
}`;

export const BUY_WITH_CARD_MUTATION = gql`mutation BuyWithCard($productId:ID!,$quantity:Int,$paymentIntentId:String!,$deliveryAddress:String) {
  buyProductWithCard(productId:$productId,quantity:$quantity,paymentIntentId:$paymentIntentId,deliveryAddress:$deliveryAddress) {
    id totalCoins totalAmount currency paymentMethod status product { id name imageEmoji imageUrl }
  }
}`;

export const CASHOUT_COINS_MUTATION = gql`mutation CashoutCoins($coins:Int!) {
  cashoutCoins(coins:$coins) { id coins amount currency status createdAt }
}`;

/* ═══════════════════════════════════════ MARKETPLACE: ACCOUNTS / LEDGERS / RATINGS ═══ */

const ORDER_FIELDS = `id productId userId quantity totalCoins totalAmount currency paymentMethod status deliveryAddress rating feedback ratedAt createdAt
  productName sellerId sellerName buyerName
  product { id name imageEmoji imageUrl price productType }`;

// Buyer: single account summary + sellers list + full order history (with ratings).
export const MY_ORDERS_FULL_QUERY = gql`query MyOrdersFull {
  myBuyerAccount { orderCount sellerCount totalAmount totalCoins }
  buyerSellers { sellerId name avatarColor storePersona orderCount totalAmount totalCoins lastOrderAt myAvgRating sellerAvgRating }
  myOrders { ${ORDER_FIELDS} }
}`;

export const BUYER_SELLER_LEDGER_QUERY = gql`query BuyerSellerLedger($sellerId:ID!) {
  buyerSellerLedger(sellerId:$sellerId) { ${ORDER_FIELDS} }
}`;

export const RATE_ORDER_MUTATION = gql`mutation RateOrder($orderId:ID!,$rating:Int!,$feedback:String) {
  rateOrder(orderId:$orderId,rating:$rating,feedback:$feedback) { ${ORDER_FIELDS} }
}`;

export const REORDER_MUTATION = gql`mutation Reorder($orderId:ID!,$quantity:Int) {
  reorder(orderId:$orderId,quantity:$quantity) { ${ORDER_FIELDS} }
}`;

// Seller: account + orders + customers, plus P&L and public ratings.
export const SELLER_DASHBOARD_QUERY = gql`query SellerDashboard {
  mySellerAccount { orderCount customerCount totalAmount totalCoins }
  sellerOrders { ${ORDER_FIELDS} }
  sellerCustomers { userId name avatarColor city orderCount totalAmount totalCoins lastOrderAt avgRating }
}`;

export const SELLER_ORDERS_QUERY = gql`query SellerOrders($status:String,$from:String,$to:String) {
  sellerOrders(status:$status,from:$from,to:$to) { ${ORDER_FIELDS} }
}`;

export const SELLER_CUSTOMER_LEDGER_QUERY = gql`query SellerCustomerLedger($customerId:ID!) {
  sellerCustomerLedger(customerId:$customerId) { ${ORDER_FIELDS} }
}`;

export const SELLER_PROFIT_LOSS_QUERY = gql`query SellerProfitLoss($from:String,$to:String) {
  sellerProfitLoss(from:$from,to:$to) { revenue cost profit orderCount }
}`;

export const SELLER_RATINGS_QUERY = gql`query SellerRatings($sellerId:ID!) {
  sellerRatings(sellerId:$sellerId) { sellerId avg count reviews { orderId rating feedback buyerName productName createdAt } }
}`;

export const UPDATE_ORDER_STATUS_MUTATION = gql`mutation UpdateOrderStatus($orderId:ID!,$status:String!) {
  updateOrderStatus(orderId:$orderId,status:$status) { ${ORDER_FIELDS} }
}`;

export const SET_PRODUCT_COST_MUTATION = gql`mutation SetProductCost($productId:ID!,$unitCost:Int!) {
  setProductCost(productId:$productId,unitCost:$unitCost) { ${PRODUCT_FIELDS} }
}`;

// Offers of the day (public — shown in the top banner).
export const FLASH_OFFERS_QUERY = gql`query FlashOffers {
  flashOffers { id code description discountPct offerLabel sellerId sellerName sellerPersona expiresAt }
}`;

// Referrals.
export const MY_REFERRALS_QUERY = gql`query MyReferrals {
  myReferrals { code count totalCoinsEarned referrals { referredName avatarColor rewardCoins createdAt } }
}`;

export const MATCHED_GROUPS_QUERY = gql`query MatchedGroupsForExpert {
  matchedGroupsForExpert { id name category tags memberCount matchScore requestStatus }
  myExpertGroupRequests { id groupId groupName groupCategory status message createdAt }
}`;

export const APPLY_EXPERT_TO_GROUP_MUTATION = gql`mutation ApplyExpertToGroup($groupId:ID!,$message:String) {
  applyExpertToGroup(groupId:$groupId,message:$message) { id groupId groupName status message createdAt }
}`;

/* ═══════════════════════════════════════ RECOMMENDATIONS / PROFILE STRENGTH ══ */

export const RECOMMENDED_GROUPS_QUERY = gql`query RecommendedGroups($limit:Int) {
  recommendedGroups(limit:$limit) {
    matchScore matchReasons
    group { id name description category tags memberCount maxMembers isOpen isMember persona broadCategory imageUrl features location { city country building } schedule { day time nextSession } creator { id name avatarColor } }
  }
}`;

export const PROFILE_STRENGTH_QUERY = gql`query ProfileStrength { profileStrength { score missing } }`;

// Personalized dashboard feed: joined + skill/location-matched groups only.
export const DASHBOARD_GROUPS_QUERY = gql`query DashboardGroups($limit:Int) {
  dashboardGroups(limit:$limit) { ${GROUP_FIELDS} }
}`;

/* ═══════════════════════════════════════ SERVICE REQUESTS ("Post a Need") ═══════ */

const SR_FIELDS = `id userId title description persona broadCategory city budget currency neededBy status createdAt posterName posterAvatar responseCount`;
const SR_RESP_FIELDS = `id requestId responderId message quote status createdAt responderName responderAvatar responderUpi`;

export const SERVICE_REQUESTS_QUERY = gql`query ServiceRequests($city:String,$persona:String,$broadCategory:String) {
  serviceRequests(city:$city,persona:$persona,broadCategory:$broadCategory) { ${SR_FIELDS} myResponse { ${SR_RESP_FIELDS} } }
}`;
export const MY_SERVICE_REQUESTS_QUERY = gql`query MyServiceRequests {
  myServiceRequests { ${SR_FIELDS} responses { ${SR_RESP_FIELDS} } }
}`;
export const CREATE_SERVICE_REQUEST_MUTATION = gql`mutation CreateServiceRequest($title:String!,$description:String,$persona:String,$broadCategory:String,$city:String,$budget:Int,$currency:String,$neededBy:String) {
  createServiceRequest(title:$title,description:$description,persona:$persona,broadCategory:$broadCategory,city:$city,budget:$budget,currency:$currency,neededBy:$neededBy) { ${SR_FIELDS} }
}`;
export const CLOSE_SERVICE_REQUEST_MUTATION = gql`mutation CloseServiceRequest($id:ID!) { closeServiceRequest(id:$id) { id status } }`;
export const RESPOND_SERVICE_REQUEST_MUTATION = gql`mutation RespondServiceRequest($requestId:ID!,$message:String,$quote:Int) {
  respondToServiceRequest(requestId:$requestId,message:$message,quote:$quote) { ${SR_RESP_FIELDS} }
}`;
export const ACCEPT_SERVICE_RESPONSE_MUTATION = gql`mutation AcceptServiceResponse($responseId:ID!) { acceptServiceResponse(responseId:$responseId) }`;

/* ═══════════════════════════════════════ HOBBYSOCIAL (community feed) ═════ */

const POST_FIELDS = `id authorId type title content imageUrl videoUrl tags persona likeCount commentCount likedByMe moderationStatus createdAt authorName authorAvatar`;
const POST_COMMENT_FIELDS = `id postId authorId content createdAt authorName authorAvatar`;

export const SOCIAL_FEED_QUERY = gql`query SocialFeed($limit:Int,$offset:Int) {
  socialFeed(limit:$limit,offset:$offset) { ${POST_FIELDS} }
}`;
export const MY_POSTS_QUERY = gql`query MyPosts { myPosts { ${POST_FIELDS} } }`;
export const POST_QUERY = gql`query Post($id:ID!) {
  post(id:$id) { ${POST_FIELDS} comments { ${POST_COMMENT_FIELDS} } }
}`;
export const CREATE_POST_MUTATION = gql`mutation CreatePost($type:String,$title:String,$content:String,$imageUrl:String,$videoUrl:String,$tags:[String!],$persona:String) {
  createPost(type:$type,title:$title,content:$content,imageUrl:$imageUrl,videoUrl:$videoUrl,tags:$tags,persona:$persona) { ${POST_FIELDS} }
}`;
export const LIKE_POST_MUTATION = gql`mutation LikePost($id:ID!) { likePost(id:$id) { id likeCount likedByMe } }`;
export const ADD_POST_COMMENT_MUTATION = gql`mutation AddPostComment($postId:ID!,$content:String!) {
  addPostComment(postId:$postId,content:$content) { ${POST_COMMENT_FIELDS} }
}`;
export const DELETE_POST_MUTATION = gql`mutation DeletePost($id:ID!) { deletePost(id:$id) }`;

/* ═══════════════════════════════════════ EXPERT DOCUMENTS / CAREER LAUNCH ═════ */

const DOC_FIELDS = `id expertId docType fileName fileUrl notes status reviewedBy reviewNote uploadedAt reviewedAt`;

export const MY_EXPERT_DOCUMENTS_QUERY = gql`query MyExpertDocuments { myExpertDocuments { ${DOC_FIELDS} } }`;

export const EXPERT_DOCUMENTS_QUERY = gql`query ExpertDocuments($expertId:ID!) { expertDocuments(expertId:$expertId) { ${DOC_FIELDS} } }`;

export const UPLOAD_EXPERT_DOCUMENT_MUTATION = gql`mutation UploadExpertDocument($docType:DocType!,$fileName:String!,$fileUrl:String!,$notes:String) {
  uploadExpertDocument(docType:$docType,fileName:$fileName,fileUrl:$fileUrl,notes:$notes) { ${DOC_FIELDS} }
}`;

export const DELETE_EXPERT_DOCUMENT_MUTATION = gql`mutation DeleteExpertDocument($docId:ID!) { deleteExpertDocument(docId:$docId) }`;

export const REVIEW_EXPERT_DOCUMENT_MUTATION = gql`mutation ReviewExpertDocument($docId:ID!,$status:DocStatus!,$reviewNote:String) {
  reviewExpertDocument(docId:$docId,status:$status,reviewNote:$reviewNote) { ${DOC_FIELDS} }
}`;

export const EXPERT_ONBOARDING_QUERY = gql`query ExpertOnboarding($expertId:ID!,$groupId:ID!) {
  expertOnboarding(expertId:$expertId,groupId:$groupId) { expertId groupId currentStep completedSteps startedAt completedAt }
}`;

export const ADVANCE_ONBOARDING_STEP_MUTATION = gql`mutation AdvanceOnboardingStep($expertId:ID!,$groupId:ID!,$step:OnboardingStep!) {
  advanceOnboardingStep(expertId:$expertId,groupId:$groupId,step:$step) { expertId groupId currentStep completedSteps startedAt completedAt }
}`;

export const CAREER_LAUNCH_PIPELINE_QUERY = gql`query CareerLaunchPipeline($expertId:ID!,$groupId:ID) {
  careerLaunchPipeline(expertId:$expertId,groupId:$groupId) {
    expertId groupId overallStatus percentComplete
    stages { stage label status completedAt details }
  }
}`;

export const REVIEW_EXPERT_REQUEST_MUTATION = gql`mutation ReviewExpertRequest($expertId:ID!,$groupId:ID!,$status:String!) {
  reviewExpertGroupRequest(expertId:$expertId,groupId:$groupId,status:$status)
}`;

export const PENDING_EXPERT_REQUESTS_QUERY = gql`query PendingExpertRequests($groupId:ID!) {
  pendingExpertRequestsForGroup(groupId:$groupId) {
    id expertId groupId status message createdAt
    expertHeadline expertSkills expertServiceType expertHourlyRate expertCurrency
    userName userId avatarColor
  }
}`;

export const SEARCH_PLATFORM_USERS_QUERY = gql`query SearchPlatformUsers($search:String!,$groupId:ID!) {
  searchPlatformUsers(search:$search,groupId:$groupId) { id name email avatarColor city role ageGroup isMember }
}`;

export const FIND_EXPERTS_FOR_GROUP_QUERY = gql`query FindExpertsForGroup($groupId:ID!,$search:String) {
  findExpertsForGroup(groupId:$groupId,search:$search) { id userId name avatarColor headline skills serviceType hourlyRate currency ratingAvg ratingCount isElderSupport isVerified matchScore alreadyRequested }
}`;

export const GROUP_INVITATIONS_QUERY = gql`query GroupInvitations($groupId:ID!) {
  groupInvitations(groupId:$groupId) { id groupId invitedUserId userName avatarColor userRole message status createdAt }
}`;

export const MY_GROUP_INVITATIONS_QUERY = gql`query MyGroupInvitations {
  myGroupInvitations { id groupId groupName groupCategory inviterName message status createdAt }
}`;

export const INVITE_USER_MUTATION = gql`mutation InviteUser($groupId:ID!,$userId:ID!,$message:String) {
  inviteUserToGroup(groupId:$groupId,userId:$userId,message:$message)
}`;

export const INVITE_EXPERT_MUTATION = gql`mutation InviteExpert($groupId:ID!,$expertId:ID!,$message:String) {
  inviteExpertToGroup(groupId:$groupId,expertId:$expertId,message:$message)
}`;

export const ACCEPT_INVITE_MUTATION = gql`mutation AcceptInvite($groupId:ID!) {
  acceptGroupInvitation(groupId:$groupId)
}`;

// ── i18n / Globalisation ──────────────────────────────────────────────────────
export const UPDATE_LOCALE_MUTATION = gql`mutation UpdateLocale($language:String,$currency:String,$locale:String,$timezone:String) {
  updateLocale(language:$language,currency:$currency,locale:$locale,timezone:$timezone) {
    id language currency locale location { timezone }
  }
}`;

/* ═══════════════════════════════════════ COMPANY ══════════════════════════ */

export const REGISTER_COMPANY_MUTATION = gql`
  mutation RegisterCompany($name: String!, $slug: String!, $description: String, $industry: String, $sizeBand: String, $logoUrl: String, $brandColor: String, $tagline: String, $website: String) {
    registerCompany(name: $name, slug: $slug, description: $description, industry: $industry, sizeBand: $sizeBand, logoUrl: $logoUrl, brandColor: $brandColor, tagline: $tagline, website: $website) {
      id name slug brandColor logoUrl tagline
    }
  }
`;

export const CHECK_SLUG_AVAILABLE_QUERY = gql`
  query CheckSlugAvailable($slug: String!) {
    checkSlugAvailable(slug: $slug) {
      available
      reason
    }
  }
`;

export const PENDING_COMPANIES_QUERY = gql`
  query PendingCompanies {
    pendingCompanies {
      id name slug status brandColor logoUrl tagline industry sizeBand createdAt
      admins { id name email avatarColor }
    }
  }
`;

export const APPROVE_COMPANY_MUTATION = gql`
  mutation ApproveCompany($companyId: ID!) {
    approveCompany(companyId: $companyId) { id name slug status }
  }
`;

export const REJECT_COMPANY_MUTATION = gql`
  mutation RejectCompany($companyId: ID!, $reason: String) {
    rejectCompany(companyId: $companyId, reason: $reason) { id name slug status }
  }
`;

export const MY_COMPANY_QUERY = gql`
  query MyCompany {
    myCompany {
      id name slug description logoUrl brandColor tagline website industry sizeBand plan createdAt
      admins { id name email avatarColor }
    }
  }
`;

export const COMPANY_QUERY = gql`
  query Company($slug: String!) {
    company(slug: $slug) {
      id name slug description logoUrl brandColor tagline website industry sizeBand plan createdAt
      admins { id name avatarColor }
    }
  }
`;

export const UPDATE_COMPANY_BRANDING_MUTATION = gql`
  mutation UpdateCompanyBranding($companyId: ID!, $logoUrl: String, $brandColor: String, $tagline: String, $website: String) {
    updateCompanyBranding(companyId: $companyId, logoUrl: $logoUrl, brandColor: $brandColor, tagline: $tagline, website: $website) {
      id brandColor logoUrl tagline website
    }
  }
`;

export const ADD_COMPANY_ADMIN_MUTATION = gql`
  mutation AddCompanyAdmin($companyId: ID!, $userId: ID!) {
    addCompanyAdmin(companyId: $companyId, userId: $userId) {
      id admins { id name avatarColor }
    }
  }
`;

export const UPDATE_PROFILE_WITH_POSTAL_MUTATION = gql`mutation UpdateProfileWithPostal(
  $bio:String,$interests:[String!],$age:Int,$building:String,$neighborhood:String,
  $city:String,$country:String,$postalCode:String,$language:String,$theme:Theme,
  $currency:String,$locale:String,$timezone:String,$lat:Float,$lng:Float
) {
  updateProfile(bio:$bio,interests:$interests,age:$age,building:$building,neighborhood:$neighborhood,
    city:$city,country:$country,postalCode:$postalCode,language:$language,theme:$theme,
    currency:$currency,locale:$locale,timezone:$timezone,lat:$lat,lng:$lng) {
    ${USER_FIELDS.replace(/\n/g, ' ')}
    joinedGroups { id name category memberCount maxMembers isOpen }
  }
}`;

export const PUBLIC_FOOD_ITEMS_QUERY = gql`query PublicFoodItems {
  publicFoodItems { id groupId name description price productType imageEmoji imageUrl availableOutside pickupAddress pickupLat pickupLng foodCategory stock persona createdAt creator { id name avatarColor sellerRatingAvg sellerRatingCount } }
}`;
